/**
 * Granuláty live price — converts the CZK/oz spot feed into the
 * granule sell price (spot + margin) and renders it on the page.
 *
 * Gold is quoted per gram, silver per kilogram. Prices exclude VAT.
 * The unit price is rounded once and then multiplied by weight, so a
 * customer hand-checking the total always gets the displayed number.
 */
(function () {
  'use strict';

  var CACHE_KEY = 'avarrio_prices'; // shared with price-feed.js — no duplicate fetch
  var CACHE_MAX_AGE = 10 * 60 * 1000;
  // The backend ingests a fresh rate every 5 minutes; 30 minutes tolerates
  // five consecutive missed runs (a transient upstream hiccup) while still
  // bounding a "confirmed on reply" quote to something a goldsmith would
  // consider fair — gold moves little in half an hour. Deliberately larger
  // than CACHE_MAX_AGE (which only governs our own localStorage cache): the
  // defect this guards against is *unbounded* staleness, not a few missed runs.
  var FEED_READING_MAX_AGE = 30 * 60 * 1000;
  var CACHE_FRESH_AGE = 3 * 60 * 1000;
  var POLL_INTERVAL = 60 * 1000;
  var FETCH_TIMEOUT = 5000;
  var GRAMS_PER_TROY_OUNCE = 31.1035;

  var root = document.querySelector('[data-granulaty-prices]');
  if (!root) return;

  var apiUrl = root.getAttribute('data-price-api-url');
  var marginPct = parseFloat(root.getAttribute('data-margin-pct'));
  var vatPct = parseFloat(root.getAttribute('data-vat-pct'));
  if (!apiUrl) return;
  if (isNaN(marginPct)) marginPct = 2;
  if (isNaN(vatPct)) vatPct = 21;

  var intervalId = null;
  var wasAvailable = undefined; // tracks previous availability to detect transitions

  function perGramFromOunce(czkPerOunce) {
    return Math.round(czkPerOunce / GRAMS_PER_TROY_OUNCE);
  }

  function goldPerGram(czkPerOunce, margin) {
    return Math.round((czkPerOunce / GRAMS_PER_TROY_OUNCE) * (1 + margin / 100));
  }

  function silverPerKilogram(czkPerOunce, margin) {
    return Math.round((czkPerOunce / GRAMS_PER_TROY_OUNCE) * (1 + margin / 100) * 1000);
  }

  function withVat(value, vat) {
    return Math.round(value * (1 + vat / 100));
  }

  function formatCzk(value) {
    return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  /** True when `date` falls on today's calendar date in Europe/Prague. */
  function isTodayInPrague(date) {
    var fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Prague',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return fmt.format(date) === fmt.format(new Date());
  }

  /**
   * Formats the price timestamp in Europe/Prague, regardless of the
   * visitor's browser timezone. A reading that isn't from today also gets
   * its date prefixed, so a stale-looking value is legible as such even
   * when it's inside the freshness window (e.g. just after midnight).
   */
  function formatUpdatedAt(date) {
    var time = new Intl.DateTimeFormat('cs-CZ', {
      timeZone: 'Europe/Prague',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);

    if (isTodayInPrague(date)) return time;

    var day = new Intl.DateTimeFormat('cs-CZ', {
      timeZone: 'Europe/Prague',
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    }).format(date);

    return day + ' ' + time;
  }

  /**
   * Same time formatting as formatUpdatedAt, but the date is always
   * included — for the enquiry email's spot field, which may be read hours
   * or days later, so a same-day quote must not read as a bare "9:12".
   * The on-page timestamp lines stay conditional (formatUpdatedAt); this is
   * for email use only.
   */
  function formatUpdatedAtWithDate(date) {
    var time = new Intl.DateTimeFormat('cs-CZ', {
      timeZone: 'Europe/Prague',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);

    var day = new Intl.DateTimeFormat('cs-CZ', {
      timeZone: 'Europe/Prague',
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    }).format(date);

    return day + ' ' + time;
  }

  /** Rejects anything but a finite numeric spot value — null, missing or string all fail. */
  function isValidPrice(price) {
    return !!price && typeof price.value === 'number' && isFinite(price.value);
  }

  var api = {
    GRAMS_PER_TROY_OUNCE: GRAMS_PER_TROY_OUNCE,
    perGramFromOunce: perGramFromOunce,
    goldPerGram: goldPerGram,
    silverPerKilogram: silverPerKilogram,
    withVat: withVat,
    formatCzk: formatCzk,
    formatUpdatedAt: formatUpdatedAt,
    formatUpdatedAtWithDate: formatUpdatedAtWithDate,
    marginPct: marginPct,
    vatPct: vatPct,
    state: { available: false, gold: null, silver: null, updatedAt: null }
  };

  window.avarrio = window.avarrio || {};
  window.avarrio.granulaty = api;

  function getCached() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (Date.now() - cached._cachedAt > CACHE_MAX_AGE) return null;
      return cached;
    } catch (e) {
      return null;
    }
  }

  function isCacheFresh() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return false;
      var cached = JSON.parse(raw);
      return Date.now() - cached._cachedAt <= CACHE_FRESH_AGE;
    } catch (e) {
      return false;
    }
  }

  function setCache(data) {
    try {
      data._cachedAt = Date.now();
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (e) {
      // localStorage full or unavailable — fall through, we just refetch
    }
  }

  function setText(key, value) {
    document.querySelectorAll('[data-granulaty-price="' + key + '"]').forEach(function (el) {
      el.textContent = value;
    });
  }

  function setAvailability(available) {
    document.querySelectorAll('[data-granulaty-price-block]').forEach(function (el) {
      el.classList.toggle('is-price-unavailable', !available);
    });
  }

  function announce() {
    if (typeof publish === 'function') publish('granulaty:price-update', api.state);
  }

  function render(data) {
    var gold = data.prices && data.prices.gold;
    var silver = data.prices && data.prices.silver;
    if (!isValidPrice(gold) || !isValidPrice(silver)) {
      renderUnavailable();
      return;
    }

    var updatedAt = new Date(data.timestamp);
    if (isNaN(updatedAt.getTime())) updatedAt = null; // malformed feed timestamp — never surface an Invalid Date

    // The backend returns its newest stored row unconditionally, even if the
    // rate-ingest job has died — HTTP 200 with a days-old payload. Freshness
    // must be judged by when the price was struck (data.timestamp), never by
    // when *we* fetched it. Data of unknown or excessive age is stale data
    // and must never render as a current price.
    var age = updatedAt ? Date.now() - updatedAt.getTime() : Infinity;
    if (!updatedAt || age > FEED_READING_MAX_AGE) {
      renderUnavailable();
      return;
    }

    api.state = {
      available: true,
      gold: {
        perGram: goldPerGram(gold.value, marginPct),
        spotPerGram: perGramFromOunce(gold.value),
        spotPerOunce: gold.value,
        changePct: gold.change_pct
      },
      silver: {
        perKilogram: silverPerKilogram(silver.value, marginPct),
        spotPerKilogram: Math.round(silver.value / GRAMS_PER_TROY_OUNCE * 1000),
        spotPerOunce: silver.value,
        changePct: silver.change_pct
      },
      updatedAt: updatedAt
    };

    setText('gold-unit', formatCzk(api.state.gold.perGram));
    setText('gold-spot-unit', formatCzk(api.state.gold.spotPerGram));
    setText('gold-unit-vat', formatCzk(withVat(api.state.gold.perGram, vatPct)));
    setText('gold-example-100g', formatCzk(api.state.gold.perGram * 100));
    setText('gold-example-spot-100g', formatCzk(api.state.gold.spotPerGram * 100));
    setText('silver-unit', formatCzk(api.state.silver.perKilogram));
    setText('silver-spot-unit', formatCzk(api.state.silver.spotPerKilogram));
    setText('silver-unit-vat', formatCzk(withVat(api.state.silver.perKilogram, vatPct)));
    setText('updated-at', formatUpdatedAt(api.state.updatedAt));

    setAvailability(true);
    announce();
    wasAvailable = true;
  }

  function renderUnavailable() {
    api.state = { available: false, gold: null, silver: null, updatedAt: null };
    setAvailability(false);
    // Only announce on transition into unavailable (not repeatedly while unavailable)
    if (wasAvailable !== false) {
      announce();
      wasAvailable = false;
    }
  }

  function fetchPrices() {
    if (isCacheFresh()) return;

    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT);

    fetch(apiUrl, { signal: controller.signal })
      .then(function (res) {
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data.prices) throw new Error('Invalid response');
        setCache(data);
        render(data);
      })
      .catch(function () {
        clearTimeout(timeoutId);
        var cached = getCached();
        if (cached) render(cached);
        else renderUnavailable();
      });
  }

  var cached = getCached();
  if (cached) render(cached);
  else renderUnavailable();

  fetchPrices();
  intervalId = setInterval(fetchPrices, POLL_INTERVAL);

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      clearInterval(intervalId);
      intervalId = null;
    } else {
      // Cache may have expired while hidden — re-evaluate before refetching.
      if (!getCached()) renderUnavailable();
      fetchPrices();
      intervalId = setInterval(fetchPrices, POLL_INTERVAL);
    }
  });
})();
